import { queryOptions, useQueries, useQuery } from '@tanstack/react-query'
import { useCurrentUser, useWorkspace } from 'components/providers'
import { where } from 'firebase/firestore'
import { useCallback } from 'react'
import {
  NavigateOptions,
  createSearchParams,
  generatePath,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import {
  IUser,
  IWorkspace,
  OmitDBProps,
  Permission,
  User,
  WorkspaceScope,
  WorkspaceUser,
} from 'types'
import { getDocById, getDocs } from './db'
import { useEntevatePermission } from './preferences'

export const WorkspacePath = '/:workspaceId'

/** Go to a workspace */
export function useGoToWorkspace() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  function goToWorkspace(
    workspaceId?: string,
    search?: { [prop: string]: string } | null,
    options?: NavigateOptions
  ) {
    if (!workspaceId) return

    let _search = searchParams.toString()
    if (search) _search = createSearchParams(search).toString()

    navigate({ pathname: generatePath(WorkspacePath, { workspaceId }), search: _search }, options)
  }

  return goToWorkspace
}

export function useSwitchWorkspace() {
  const navigate = useNavigate()

  function switchWorkspace(workspaceId?: string, routePath?: string, options?: NavigateOptions) {
    if (!workspaceId) return

    navigate({ pathname: generatePath(`${WorkspacePath}/${routePath}`, { workspaceId }) }, options)
  }

  return switchWorkspace
}

/** Go back or to root of workspace if default route */
export default function useGoBack() {
  const { workspaceId } = useParams()
  const goToWorkspace = useGoToWorkspace()
  const location = useLocation()
  const navigate = useNavigate()

  function goBack() {
    const firstRoute = location.key === 'default'
    if (firstRoute) goToWorkspace(workspaceId)
    else navigate(-1)
  }

  return goBack
}

export const makeWorkspacesKey = (scope: WorkspaceScope, userId?: string) =>
  ['workspaces', scope, userId] as [string, WorkspaceScope, string]
export const makeWorkspaceKey = (workspaceId?: string) => ['workspace', workspaceId]

export function useWorkspaces(scope: WorkspaceScope) {
  const user = useCurrentUser()
  const userId = user?.isAdmin ? undefined : user?.id

  const { data, ...rest } = useQuery({
    queryKey: makeWorkspacesKey(scope, userId),
    queryFn: () => getWorkspaces(scope, userId),
  })

  return { workspaces: data, ...rest }
}

async function getWorkspaces(scope: WorkspaceScope, userId?: string) {
  const scopeConstraint = where('scope', '==', scope)
  const archiveConstraint = where('isArchived', '==', false)
  if (!userId) return getDocs('Workspace', 0, scopeConstraint, archiveConstraint)
  return getDocs('Workspace', 0, scopeConstraint, where('userIds', 'array-contains', userId))
}

export const makeEmptyWorkspace = (scope: IWorkspace['scope']): OmitDBProps<IWorkspace> => ({
  pendingUserByEmail: {},
  scope,
  title: '',
  isArchived: false,
  userById: {},
  userIds: [],
  traitTagById: {},
})

export function usePermission(): Permission {
  const { workspace } = useWorkspace()
  const user = useCurrentUser()
  const [entevatePermission] = useEntevatePermission()

  if (!workspace || !user) return {}

  if (user.isAdmin) {
    const permission = entevatePermission === 'entevate' ? 'admin' : entevatePermission
    return { content: permission, event: permission, facility: permission, workspace: permission }
  }

  const workspacePermission = workspace.userById[user.id]?.permission

  return {
    content: workspacePermission,
    event: workspacePermission,
    facility: workspacePermission,
    workspace: workspacePermission,
  }
}

export function useUser(id?: string) {
  const options = useUserQueryOptions()
  return useQuery(options(id))?.data
}

export function useUsers(userIds: string[]) {
  const options = useUserQueryOptions()
  return useQueries({
    queries: userIds.map(options),
    combine: res => ({
      data: res.map(r => r.data).filter(Boolean),
      isLoading: res.some(r => r.isLoading),
    }),
  })
}

function useUserQueryOptions() {
  const { workspace } = useWorkspace()
  const select = useCallback((dbUser: IUser | null) => makeUser(dbUser, workspace), [workspace])

  return (id?: string) =>
    queryOptions({
      queryKey: ['users', workspace, id],
      queryFn: () => getDocById('users', id!),
      enabled: !!id,
      staleTime: 1000 * 60,
      select,
    })
}

export function makeUser(userDoc: IUser | WorkspaceUser | null, workspace?: IWorkspace | null) {
  if (!userDoc || !workspace) return

  const displayName = makeUserDisplayName(userDoc)

  const pendingUser = workspace.pendingUserByEmail[userDoc.email]
  if (pendingUser) return { ...userDoc, ...pendingUser, displayName, status: 'pending' } as User

  if (!('id' in userDoc)) throw new Error('workspace users must have a User document')

  const workspaceUser = workspace.userById[userDoc.id]
  const status: 'active' | 'pending' = workspaceUser ? 'active' : 'pending'
  return { ...userDoc, ...workspaceUser, displayName, status } as User
}

export const makeUserDisplayName = (user?: Partial<IUser> | null) =>
  user?.name || user?.email || user?.id
